import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import TransactionsContext, { TransactionsCtx } from './TransactionsContext';
import TransactionsDialogue from './TransactionsDialogue';
import { DialogueHandlerProps, TransactionDialogueHandlers } from './types';
import PartnerAccountTransfer from './PartnerAccountTransfer';
import usePostPartnerAccountTransfer from '../../hooks/useApi/usePostPartnerAccountTransfer';
import usePostPartnerAccountFundAndTransfer from '../../hooks/useApi/usePostPartnerAccountFundAndTransfer';
import usePostPartnerAccountTransferAndWithdraw from '../../hooks/useApi/usePostPartnerAccountTransferAndWithdraw';
import usePostPartnerAccountFundTransferAndWithdraw from '../../hooks/useApi/usePostPartnerAccountFundTransferAndWithdraw';
import useDataFetchIndicator from '../../hooks/useDataFetchIndicator';

const PartnerAccountTransfers = (): React.JSX.Element => {
  const [showTransactionsSearchSuccessAlert, setShowTransactionsSearchSuccessAlert] = useState(false);
  const [showTransactionsSearchErrorAlert, setShowTransactionsSearchErrorAlert] = useState(false);
  const [transactionsSearchStatus, setTransactionsSearchStatus] = useState<boolean | null>(null);
  const [transactionsSuccessMessage, setTransactionsSuccessMessage] = useState<string>('');
  const [transactionsSearchError, setTransactionsSearchError] = useState<string>('');

  const [, postPartnerAccountTransfer] = usePostPartnerAccountTransfer({});
  const [, postPartnerAccountFundAndTransfer] = usePostPartnerAccountFundAndTransfer({});
  const [, postPartnerAccountTransferAndWithdraw] = usePostPartnerAccountTransferAndWithdraw({});
  const [, postPartnerAccountFundTransferAndWithdraw] = usePostPartnerAccountFundTransferAndWithdraw({});
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const [isReview, setIsReview] = useState<boolean>(false);
  const [isBack, setIsBack] = useState<boolean>(false);
  const history = useHistory();

  const [accountTransferResponse, setAccountTransferResponse] = useState<
    | vp.PartnerAccountTransferResponse
    | vp.PartnerAccountFundAndTransferResponse
    | vp.PartnerAccountTransferAndWithdrawResponse
    | vp.PartnerAccountFundTransferAndWithdrawResponse
    | { Success: boolean; ErrorMessage: string }
  >({
    Success: false,
    ErrorMessage: '',
    TransactionID: '',
  });
  const [defaultValues, setDefaultValues] = useState<vp.PartnerAccountTransferRequest>({
    TransferTypeID: '',
    DebitorAccountID: '',
    RecipientAccountID: '',
    Amount: '',
    Currency: '',
    ClientReferenceNumber: '',
    Notes: '',
  });
  const [accountTransferValues, setAccountTransferValues] = useState<vp.PartnerAccountTransferRequest>({
    TransferTypeID: '',
    DebitorAccountID: '',
    RecipientAccountID: '',
    Amount: '',
    Currency: '',
    ClientReferenceNumber: '',
    Notes: '',
  });

  const [showLoader, setShowLoader] = useState<boolean>(false);

  useEffect(() => {
    if (transactionsSearchStatus === false) {
      setShowTransactionsSearchSuccessAlert(false);
      setShowTransactionsSearchErrorAlert(true);
    }
  }, [transactionsSearchStatus]);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<TransactionDialogueHandlers>) => {
      const { type, state, action = 'default', hasNavigation } = handlerProps;

      const dialogueHandlers: TransactionDialogueHandlers = {
        search: {
          success: {
            default: () => {
              setShowTransactionsSearchSuccessAlert(false);
              setShowTransactionsSearchErrorAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowTransactionsSearchSuccessAlert(false);
              setShowTransactionsSearchErrorAlert(false);
              setTransactionsSearchStatus(null);
            },
          },
        },
      };

      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();

      if (hasNavigation) {
        setDefaultValues({
          TransferTypeID: '',
          DebitorAccountID: '',
          RecipientAccountID: '',
          Amount: '',
          Currency: '',
          ClientReferenceNumber: '',
          Notes: '',
        });
        history.push('/partner-account-transfers');
        setIsBack(true);
        setIsReview(false);
      } else {
        setIsBack(false);
        setIsReview(true);
      }
    },

    handleReview: (values: vp.PartnerAccountTransferRequest) => {
      setAccountTransferValues(values);
      setIsReview(true);
    },

    handleBack: (values: vp.PartnerAccountTransferRequest) => {
      setDefaultValues(values);
      setIsBack(true);
      setIsReview(false);
    },

    handleSubmit: async (values: vp.PartnerAccountTransferRequest) => {
      const updatedValues = { ...values };
      setShowLoader(true);
      const debitAccount = values.DebitorAccountID.split('-');
      updatedValues.DebitorAccountID = debitAccount[0] ?? '';

      switch (values.TransferTypeID) {
        case 'Transfer': {
          const response = await postPartnerAccountTransfer(updatedValues);
          setAccountTransferResponse(response);
          break;
        }
        case 'FundAndTransfer': {
          const response = await postPartnerAccountFundAndTransfer(updatedValues);
          setAccountTransferResponse(response);
          break;
        }
        case 'TransferAndWithdraw': {
          const response = await postPartnerAccountTransferAndWithdraw(updatedValues);
          setAccountTransferResponse(response);
          break;
        }
        case 'FundTransferAndWithdraw': {
          const response = await postPartnerAccountFundTransferAndWithdraw(updatedValues);
          setAccountTransferResponse(response);
          break;
        }
        default:
          setAccountTransferResponse({
            Success: false,
            ErrorMessage: 'Invalid Transaction Type',
          });
      }
      setShowLoader(false);
    },
  };

  useEffect(() => {
    if (showLoader) {
      setDataFetchingInfo(true, 'processing', 'fullScreen');
    } else {
      setDataFetchingInfo(false);
    }
  }, [showLoader]);

  useEffect(() => {
    if (accountTransferResponse.Success) {
      setTransactionsSearchStatus(true);
      setShowTransactionsSearchSuccessAlert(true);
      setTransactionsSuccessMessage('The amount has been transferred successfully');
    } else if (!accountTransferResponse.Success) {
      if (accountTransferResponse.ErrorMessage !== '') {
        setTransactionsSearchStatus(false);
        setTransactionsSearchError(accountTransferResponse.ErrorMessage);
      }
    }
  }, [accountTransferResponse]);

  useEffect(() => {
    setIsReview(false);
  }, [isBack]);

  const transactionsContext: TransactionsCtx = {
    transactionsSearchStatus,
    transactionsSuccessMessage,
    transactionsSearchError,
    showTransactionsSearchSuccessAlert,
    showTransactionsSearchErrorAlert,
  };

  return (
    <TransactionsContext.Provider value={transactionsContext}>
      <PartnerAccountTransfer
        defaultValues={isReview ? accountTransferValues : defaultValues}
        isReview={isReview}
        handlers={handlers}
      />
      <TransactionsDialogue handleDialogue={handlers.handleDialogue} />
    </TransactionsContext.Provider>
  );
};

export default PartnerAccountTransfers;
